// DigitalTaskInformation.tsx
import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { buildInitialValues, getTypeConfigs, splitPatchesForSave } from 'components/Tasks/field-utils'
import React from 'react'
import { BatchUpdateItem, OrganizationTableItem, Task } from '../../types'
import NavigateToOrg from './NavigateToOrg'
import './style.css'
interface TaskDetailsProps {
  task: Task
  isTaskLoading: boolean
  organization: OrganizationTableItem
  onNavigate: (route: string) => void
  registerTypeUpdateBuilder?: (fn: () => BatchUpdateItem[]) => void
}

const DigitalTaskInformation: React.FC<TaskDetailsProps> = ({
  task,
  isTaskLoading,
  organization,
  onNavigate,
  registerTypeUpdateBuilder,
}) => {
  const { top: topCfg, extra: extraCfg } = React.useMemo(() => getTypeConfigs(task), [task?.type])
  // local draft for *type* fields (separate from addtlNotes / checklist)
  const initialTypeVals = React.useMemo(() => buildInitialValues(task, topCfg, extraCfg), [task, topCfg, extraCfg])
  const [typeDraft, setTypeDraft] = React.useState<Record<string, any>>(initialTypeVals)

  // keep in sync when task changes
  React.useEffect(() => setTypeDraft(initialTypeVals), [initialTypeVals])

  const onTypeChange = (key: string, val: any) => setTypeDraft(prev => ({ ...prev, [key]: val }))

  // 🔽 the batch builder (diffs current draft vs task)
  const buildTypeUpdates = React.useCallback((): BatchUpdateItem[] => {
    const { topPatch, extraPatch } = splitPatchesForSave(task, typeDraft, topCfg, extraCfg)
    const updates: BatchUpdateItem[] = []

    for (const [fieldName, newValue] of Object.entries(topPatch)) {
      updates.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK, // must start with "TASK_" in your API
        fieldName,
        newValue,
      })
    }

    if (Object.keys(extraPatch).length) {
      updates.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK,
        fieldName: 'extraFields',
        newValue: { ...(task.extraFields ?? {}), ...extraPatch }, // merge client-side
      })
    }
    return updates
  }, [task, typeDraft, topCfg, extraCfg])

  React.useEffect(() => {
    registerTypeUpdateBuilder?.(() => buildTypeUpdates())
  }, [registerTypeUpdateBuilder, buildTypeUpdates])
  return isTaskLoading ? (
    <CircularProgress />
  ) : (
    <Grid container>
      <Grid item xs={12}>
        <Box sx={{ display: 'inline-flex', width: '100%', alignItems: 'center', p: 1 }}>
          <Typography sx={{ color: 'text.secondary' }}>Organization</Typography>
          <NavigateToOrg task={task} organization={organization} handleNavigate={onNavigate} />
        </Box>
        <Divider flexItem orientation="horizontal" />
      </Grid>
    </Grid>
  )
}

export default DigitalTaskInformation
